// partners/pages/PartnerDashboard.jsx
// React + Tailwind — MNF Partner Dashboard
// FIX #3 applied: Real API calls (not mock data)
// FIX #8 applied: Dynamic partner type from API response

import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
  useGetPartnerDashboardQuery,
  useGetPartnerReferralsQuery,
  useGetPartnerPayoutsQuery,
} from "../../../apis/apiSlice";
import CreditsBalance from "../../../components/CreditsBalance";
import { copyPartnerReferralLink } from "../../../utils/copyReferralLink";
import { endSession } from "../../../utils/logout";
import { capturePartnerEvent } from "../../../utils/analytics";
import { buildReferralLink, formatUSD } from "../formatMoney";

function StatCard({ label, value, sub, icon, trend, trendUp }) {
  return (
    <div className="bg-mnf-dark-3 border border-white/[0.08] rounded-mnf-lg p-5 transition-all hover:border-mnf-cyan/20">
      <div className="flex justify-between items-start mb-3">
        <div className="text-xs text-mnf-text-muted font-poppins font-semibold uppercase tracking-wider">{label}</div>
        <div className="w-9 h-9 rounded-lg bg-mnf-cyan/[0.08] flex items-center justify-center text-lg">{icon}</div>
      </div>
      <div className="font-poppins text-[28px] font-black leading-none">{value}</div>
      {sub && <div className="text-xs text-mnf-text-muted mt-1">{sub}</div>}
      {trend && (
        <div className={`text-xs mt-2 font-semibold ${trendUp ? "text-mnf-success" : "text-mnf-error"}`}>
          {trendUp ? "↑" : "↓"} {trend}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    signed_up: "bg-blue-500/10 text-blue-400",
    trial: "bg-yellow-500/10 text-yellow-400",
    converted: "bg-mnf-success/10 text-mnf-success",
    churned: "bg-mnf-error/10 text-mnf-error",
    paid: "bg-mnf-success/10 text-mnf-success",
    processing: "bg-yellow-500/10 text-yellow-400",
    calculated: "bg-blue-500/10 text-blue-400",
    failed: "bg-mnf-error/10 text-mnf-error",
  };
  return (
    <span className={`px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider ${styles[status] || "bg-white/5 text-mnf-text-muted"}`}>
      {status?.replace(/_/g, " ")}
    </span>
  );
}

export default function PartnerDashboard() {
  const [tab, setTab] = useState("overview");
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const now = new Date();
  const [payoutMonth, setPayoutMonth] = useState("");
  const [payoutYear, setPayoutYear] = useState(now.getFullYear());
  const [payoutStatus, setPayoutStatus] = useState("");

  const { data, isLoading: dashLoading, error: dashError } = useGetPartnerDashboardQuery();
  const { data: refData, isLoading: refsLoading } = useGetPartnerReferralsQuery();
  const payoutQuery = {
    year: payoutYear,
    ...(payoutMonth ? { month: payoutMonth } : {}),
    ...(payoutStatus ? { status: payoutStatus } : {}),
  };
  const { data: payData, isLoading: payoutsLoading } = useGetPartnerPayoutsQuery(payoutQuery);

  const loading = dashLoading || refsLoading || payoutsLoading;
  const referrals = refData?.results || refData || [];
  const payouts = payData?.results || payData || [];
  const payoutSummary = payData?.summary || {};

  const partner   = data?.partner  || {};
  const lifetime  = data?.stats?.lifetime || {};
  const currMonth = data?.stats?.current_month || {};

  const referralLink = useMemo(
    () => partner.referral_link || buildReferralLink(partner.referral_code),
    [partner.referral_link, partner.referral_code]
  );

  const copyLink = useCallback(async () => {
    try {
      await copyPartnerReferralLink();
      setCopyFailed(false);
      setCopied(true);
      capturePartnerEvent("partner_referral_link_copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
      setCopyFailed(true);
      capturePartnerEvent("partner_referral_link_copy_failed");
      setTimeout(() => setCopyFailed(false), 3000);
    }
  }, []);

  const switchTab = useCallback((nextTab) => {
    setTab(nextTab);
    capturePartnerEvent("partner_dashboard_tab_switched", { tab: nextTab });
  }, []);

  const viewedRef = useRef(false);
  const errorTrackedRef = useRef(false);

  useEffect(() => {
    if (loading || viewedRef.current) return;
    if (dashError || (!data && !dashLoading)) {
      if (!errorTrackedRef.current) {
        errorTrackedRef.current = true;
        const errorType =
          dashError?.status === 401 || dashError?.status === 403 ? "unauthorized" : "network";
        capturePartnerEvent("partner_dashboard_load_failed", { error_type: errorType });
      }
      return;
    }
    if (data?.partner) {
      viewedRef.current = true;
      capturePartnerEvent("partner_dashboard_viewed", {
        partner_type: data.partner.type,
        commission_rate: data.partner.commission_rate,
      });
    }
  }, [loading, dashError, data, dashLoading]);

  if (loading) {
    return (
      <div className="bg-mnf-dark-1 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-mnf-cyan border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <div className="text-mnf-text-muted font-poppins text-sm">Loading dashboard...</div>
        </div>
      </div>
    );
  }

  if (dashError || (!loading && !data)) {
    const errorMsg = dashError?.status === 401 || dashError?.status === 403
      ? "Please log in to access your dashboard."
      : "Failed to load dashboard data.";
    return (
      <div className="bg-mnf-dark-1 min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <div className="text-4xl mb-4">🔒</div>
          <h2 className="font-poppins text-xl font-bold mb-2">{errorMsg}</h2>
          <a href="/personal-login" className="text-mnf-cyan hover:underline text-sm">Go to login →</a>
        </div>
      </div>
    );
  }

  // Derived values — map nested API structure
  const partnerType    = partner.type           || "Film Ecosystem Partner";
  const commissionRate = partner.commission_rate || 30;
  const conversionRate = currMonth.conversion_rate?.toFixed(1) ?? "0.0";
  const pendingPayout  = lifetime.pending_payout  || 0;

  return (
    <div className="bg-mnf-dark-1 min-h-screen font-inter text-mnf-text-primary">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-[260px] bg-mnf-dark-2 border-r border-white/[0.08] min-h-screen p-5 shrink-0 hidden lg:flex flex-col">
          <div className="flex items-center gap-2 mb-8">
            <img src="https://cdn.prod.website-files.com/6890527cc3ef5b2eae4f1b43/68f32a89189c53cd269b54de_MNF_beta_light.svg" alt="MNF" className="h-7" />
            <span className="font-poppins text-[11px] bg-mnf-cyan/10 text-mnf-cyan px-2 py-0.5 rounded-full font-semibold">Partner</span>
          </div>

          <div className="p-4 bg-mnf-dark-3 rounded-mnf mb-6 border border-white/[0.08]">
            <div className="font-poppins text-sm font-semibold">{partner.name || "Partner"}</div>
            <div className="text-xs text-mnf-cyan mt-0.5">{partnerType}</div>
          </div>

          <nav className="space-y-1">
            {[
              { key: "overview", icon: "📊", label: "Overview" },
              { key: "referrals", icon: "🔗", label: "Referrals" },
              { key: "payouts", icon: "💰", label: "Payouts" },
            ].map((item) => (
              <button
                key={item.key}
                onClick={() => switchTab(item.key)}
                className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg font-poppins text-[13px] font-medium transition-all ${
                  tab === item.key
                    ? "bg-mnf-gradient text-mnf-btn-text"
                    : "text-mnf-text-secondary hover:bg-mnf-dark-4 hover:text-mnf-text-primary"
                }`}
              >
                <span className="text-base">{item.icon}</span> {item.label}
              </button>
            ))}
          </nav>

          <div className="mt-auto pt-8 space-y-3 border-t border-white/[0.06]">
            <button
              type="button"
              onClick={() => endSession({ page: "partner_dashboard", source: "sidebar" })}
              className="w-full flex items-center gap-2 px-3.5 py-2.5 rounded-lg font-poppins text-[13px] font-medium text-mnf-text-secondary hover:bg-mnf-dark-4 hover:text-mnf-text-primary transition-all"
            >
              <span className="text-base">↩</span> Log out
            </button>
            <a href="/become-a-partner" className="block text-xs text-mnf-text-muted hover:text-mnf-cyan transition-colors">← Partner Program Home</a>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8 max-w-[1100px]">
          <div className="flex justify-end items-center gap-4 mb-6">
            <button
              type="button"
              onClick={() => endSession({ page: "partner_dashboard", source: "mobile_header" })}
              className="lg:hidden text-xs font-poppins font-semibold text-mnf-text-muted hover:text-mnf-cyan transition-colors"
            >
              Log out
            </button>
            <CreditsBalance />
          </div>

          {/* Mobile tab bar */}
          <div className="flex gap-2 mb-6 overflow-x-auto lg:hidden pb-2">
            {["overview", "referrals", "payouts"].map((t) => (
              <button key={t} onClick={() => switchTab(t)} className={`px-4 py-2 rounded-mnf-pill font-poppins text-xs font-semibold whitespace-nowrap transition-all ${tab === t ? "bg-mnf-gradient text-mnf-btn-text" : "bg-mnf-dark-3 text-mnf-text-secondary"}`}>
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>

          {/* ===== OVERVIEW TAB ===== */}
          {tab === "overview" && (
            <>
              {/* Referral Link Bar */}
              <div className="bg-mnf-dark-3 border border-white/[0.08] rounded-mnf-lg p-5 mb-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <div className="flex-1">
                  <div className="text-xs text-mnf-text-muted font-poppins font-semibold uppercase tracking-wider mb-1">Your Referral Link</div>
                  <div className="font-mono text-sm text-mnf-cyan break-all">{referralLink}</div>
                </div>
                <button
                  onClick={copyLink}
                  className={`px-6 py-2.5 rounded-lg font-poppins text-[13px] font-semibold transition-all shrink-0 ${
                    copied
                      ? "bg-mnf-success/10 border border-mnf-success/20 text-mnf-success"
                      : copyFailed
                        ? "bg-mnf-error/10 border border-mnf-error/20 text-mnf-error"
                        : "bg-mnf-gradient text-mnf-btn-text hover:-translate-y-0.5"
                  }`}
                >
                  {copied ? "✓ Copied!" : copyFailed ? "Copy failed" : "Copy Link"}
                </button>
              </div>

              {/* Discount Code for Referrals hidden */}
              {null && partner.discount_code && (
                <div className="bg-mnf-dark-3 border border-white/[0.08] rounded-mnf-lg p-4 mb-6 flex items-center justify-between">
                  <div>
                    <div className="text-xs text-mnf-text-muted font-poppins font-semibold uppercase tracking-wider">Discount Code for Referrals</div>
                    <div className="font-poppins text-lg font-bold text-mnf-text-primary tracking-wider mt-0.5">{partner.discount_code}</div>
                  </div>
                  <div className="text-xs text-mnf-text-muted">20% off first month</div>
                </div>
              )}

              {/* Stats Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <StatCard label="Total Clicks" value={(lifetime.total_clicks || 0).toLocaleString()} icon="🔗" />
                <StatCard label="Conversions" value={(lifetime.total_conversions || 0).toLocaleString()} sub={`${conversionRate}% rate`} icon="🎯" />
                <StatCard label="Total Earnings" value={formatUSD(lifetime.total_earnings)} sub={`${commissionRate}% commission`} icon="💰" />
                <StatCard label="Next Payout" value={formatUSD(pendingPayout)} sub="Processing 1st of month" icon="📅" />
              </div>

              {/* Milestones */}
              {data.next_milestone && (
                <div className="bg-mnf-dark-3 border border-white/[0.08] rounded-mnf-lg p-5 mb-8">
                  <div className="flex justify-between items-center mb-3">
                    <div className="font-poppins text-sm font-semibold">Next Milestone: {data.next_milestone.name}</div>
                    <div className="text-xs text-mnf-success font-semibold">{formatUSD(data.next_milestone.bonus)} bonus</div>
                  </div>
                  <div className="w-full bg-mnf-dark-1 rounded-full h-2 overflow-hidden">
                    <div className="h-full bg-mnf-gradient rounded-full transition-all duration-700" style={{ width: `${Math.min(100, data.next_milestone.progress || 0)}%` }} />
                  </div>
                  <div className="text-xs text-mnf-text-muted mt-1.5">{lifetime.total_conversions || 0} / {data.next_milestone.target} referrals</div>
                </div>
              )}

              {/* Recent Referrals */}
              <div className="bg-mnf-dark-3 border border-white/[0.08] rounded-mnf-lg overflow-hidden">
                <div className="px-5 py-4 border-b border-white/[0.08] flex justify-between items-center">
                  <h3 className="font-poppins text-base font-semibold">Recent Referrals</h3>
                  <button onClick={() => switchTab("referrals")} className="text-xs text-mnf-cyan hover:underline">View all →</button>
                </div>
                {referrals.length === 0 ? (
                  <div className="p-8 text-center text-mnf-text-muted text-sm">No referrals yet. Share your link to get started!</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead><tr className="border-b border-white/[0.08]">
                        <th className="px-5 py-3 text-xs text-mnf-text-muted font-poppins font-semibold uppercase tracking-wider">User</th>
                        <th className="px-5 py-3 text-xs text-mnf-text-muted font-poppins font-semibold uppercase tracking-wider">Status</th>
                        <th className="px-5 py-3 text-xs text-mnf-text-muted font-poppins font-semibold uppercase tracking-wider">Plan</th>
                        <th className="px-5 py-3 text-xs text-mnf-text-muted font-poppins font-semibold uppercase tracking-wider">Date</th>
                      </tr></thead>
                      <tbody>
                        {referrals.slice(0, 5).map((r, i) => (
                          <tr key={r.id || i} className="border-b border-white/[0.04] hover:bg-mnf-dark-4/50 transition-colors">
                            <td className="px-5 py-3 text-sm">{r.user_email || "User"}</td>
                            <td className="px-5 py-3"><StatusBadge status={r.status} /></td>
                            <td className="px-5 py-3 text-sm text-mnf-text-secondary">{r.plan_name || "—"}</td>
                            <td className="px-5 py-3 text-sm text-mnf-text-muted">{r.signed_up_at ? new Date(r.signed_up_at).toLocaleDateString("en-IN") : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ===== REFERRALS TAB ===== */}
          {tab === "referrals" && (
            <div className="bg-mnf-dark-3 border border-white/[0.08] rounded-mnf-lg overflow-hidden">
              <div className="px-5 py-4 border-b border-white/[0.08]">
                <h3 className="font-poppins text-lg font-semibold">All Referrals</h3>
                <p className="text-xs text-mnf-text-muted mt-0.5">{referrals.length} total referrals</p>
              </div>
              {referrals.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="text-4xl mb-3">🔗</div>
                  <div className="text-mnf-text-muted">No referrals yet. Share your link to get started!</div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead><tr className="border-b border-white/[0.08]">
                      <th className="px-5 py-3 text-xs text-mnf-text-muted font-poppins font-semibold uppercase tracking-wider">User</th>
                      <th className="px-5 py-3 text-xs text-mnf-text-muted font-poppins font-semibold uppercase tracking-wider">Status</th>
                      <th className="px-5 py-3 text-xs text-mnf-text-muted font-poppins font-semibold uppercase tracking-wider">Plan</th>
                      <th className="px-5 py-3 text-xs text-mnf-text-muted font-poppins font-semibold uppercase tracking-wider">Revenue</th>
                      <th className="px-5 py-3 text-xs text-mnf-text-muted font-poppins font-semibold uppercase tracking-wider">Commission</th>
                      <th className="px-5 py-3 text-xs text-mnf-text-muted font-poppins font-semibold uppercase tracking-wider">Date</th>
                    </tr></thead>
                    <tbody>
                      {referrals.map((r, i) => (
                        <tr key={r.id || i} className="border-b border-white/[0.04] hover:bg-mnf-dark-4/50 transition-colors">
                          <td className="px-5 py-3 text-sm">{r.user_email || "User"}</td>
                          <td className="px-5 py-3"><StatusBadge status={r.status} /></td>
                          <td className="px-5 py-3 text-sm text-mnf-text-secondary">{r.plan_name || "—"}</td>
                          <td className="px-5 py-3 text-sm">{formatUSD(r.plan_price)}</td>
                          <td className="px-5 py-3 text-sm text-mnf-success">{formatUSD(r.commission)}</td>
                          <td className="px-5 py-3 text-sm text-mnf-text-muted">{r.signed_up_at ? new Date(r.signed_up_at).toLocaleDateString("en-IN") : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ===== PAYOUTS TAB ===== */}
          {tab === "payouts" && (
            <>
              <div className="flex gap-3 mb-6 flex-wrap">
                <select
                  value={payoutMonth}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    setPayoutMonth(val);
                    capturePartnerEvent("partner_payout_filter_changed", {
                      month: val || undefined,
                      year: payoutYear,
                      status: payoutStatus || undefined,
                    });
                  }}
                  className="bg-mnf-dark-3 border border-white/[0.08] text-mnf-text-secondary rounded-mnf px-3 py-2 text-sm focus:outline-none focus:border-mnf-cyan"
                >
                  <option value="">All months</option>
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {new Date(2000, i).toLocaleString("en-IN", { month: "long" })}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  value={payoutYear}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    setPayoutYear(val);
                    capturePartnerEvent("partner_payout_filter_changed", {
                      month: payoutMonth || undefined,
                      year: val,
                      status: payoutStatus || undefined,
                    });
                  }}
                  min="2024"
                  max="2030"
                  className="w-24 bg-mnf-dark-3 border border-white/[0.08] text-mnf-text-secondary rounded-mnf px-3 py-2 text-sm focus:outline-none focus:border-mnf-cyan"
                />
                <select
                  value={payoutStatus}
                  onChange={(e) => {
                    const val = e.target.value;
                    setPayoutStatus(val);
                    capturePartnerEvent("partner_payout_filter_changed", {
                      month: payoutMonth || undefined,
                      year: payoutYear,
                      status: val || undefined,
                    });
                  }}
                  className="bg-mnf-dark-3 border border-white/[0.08] text-mnf-text-secondary rounded-mnf px-3 py-2 text-sm focus:outline-none focus:border-mnf-cyan"
                >
                  <option value="">All statuses</option>
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <StatCard
                  label="Total Paid"
                  value={formatUSD(payoutSummary.paid_total ?? lifetime.total_paid)}
                  icon="✅"
                />
                <StatCard
                  label="Pending"
                  value={`$${Number(payoutSummary.pending_total ?? pendingPayout).toLocaleString("en-US", { maximumFractionDigits: 2 })}/$${Number(payoutSummary.payout_threshold ?? 80).toLocaleString("en-US", { maximumFractionDigits: 2 })}`}
                  sub="Unpaid commission / payout threshold"
                  icon="⏳"
                />
                <StatCard
                  label="Commission Rate"
                  value={`${payoutSummary.commission_rate ?? commissionRate}%`}
                  sub={
                    payoutSummary.commission_rate_label
                    || ((payoutSummary.in_first_year ?? partner.in_first_year)
                      ? "First-year rate"
                      : "Renewal rate")
                  }
                  icon="📋"
                />
              </div>
              <div className="bg-mnf-dark-3 border border-white/[0.08] rounded-mnf-lg overflow-hidden">
                <div className="px-5 py-4 border-b border-white/[0.08]">
                  <h3 className="font-poppins text-lg font-semibold">Payout History</h3>
                </div>
                {payouts.length === 0 ? (
                  <div className="p-12 text-center">
                    <div className="text-4xl mb-3">💰</div>
                    <div className="text-mnf-text-muted">No payouts yet. Payouts are processed monthly.</div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead><tr className="border-b border-white/[0.08]">
                        <th className="px-5 py-3 text-xs text-mnf-text-muted font-poppins font-semibold uppercase tracking-wider">Period</th>
                        <th className="px-5 py-3 text-xs text-mnf-text-muted font-poppins font-semibold uppercase tracking-wider">Gross</th>
                        <th className="px-5 py-3 text-xs text-mnf-text-muted font-poppins font-semibold uppercase tracking-wider">TDS</th>
                        <th className="px-5 py-3 text-xs text-mnf-text-muted font-poppins font-semibold uppercase tracking-wider">Net</th>
                        <th className="px-5 py-3 text-xs text-mnf-text-muted font-poppins font-semibold uppercase tracking-wider">Status</th>
                        <th className="px-5 py-3 text-xs text-mnf-text-muted font-poppins font-semibold uppercase tracking-wider">UTR</th>
                      </tr></thead>
                      <tbody>
                        {payouts.map((p, i) => {
                          const months = ["","Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
                          return (
                            <tr key={p.id || i} className="border-b border-white/[0.04] hover:bg-mnf-dark-4/50 transition-colors">
                              <td className="px-5 py-3 text-sm">{months[p.period_month]} {p.period_year}</td>
                              <td className="px-5 py-3 text-sm">{formatUSD(p.gross_amount)}</td>
                              <td className="px-5 py-3 text-sm text-mnf-text-muted">{formatUSD(p.tds_amount)}</td>
                              <td className="px-5 py-3 text-sm font-semibold">{formatUSD(p.net_amount)}</td>
                              <td className="px-5 py-3"><StatusBadge status={p.status} /></td>
                              <td className="px-5 py-3 text-xs text-mnf-text-muted font-mono">{p.razorpay_utr || "—"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ===== ASSETS TAB ===== */}
          {tab === "assets" && (
            <div>
              <h3 className="font-poppins text-lg font-semibold mb-6">Marketing Assets</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {[
                  { icon: "🖼️", title: "Social Media Banners", desc: "Instagram, Twitter, LinkedIn — 5 sizes", file: "MNF_Partner_Banners.zip" },
                  { icon: "📧", title: "Email Templates", desc: "Ready-to-send email copy for your audience", file: "MNF_Email_Copy.zip" },
                  { icon: "📄", title: "One-Pager PDF", desc: "Professional overview to share with prospects", file: "MNF_Partner_OnePager.pdf" },
                  { icon: "🎬", title: "ScriptPad Demo Video", desc: "2-minute product walkthrough", file: "MNF_Demo_Video.mp4" },
                  { icon: "🏷️", title: "Partner Badge", desc: "Official MNF Partner badge for your website", file: "MNF_Partner_Badge.svg" },
                  { icon: "💬", title: "Talking Points", desc: "Key messaging and value propositions", file: "MNF_Talking_Points.pdf" },
                ].map((a) => (
                  <div key={a.title} className="bg-mnf-dark-3 border border-white/[0.08] rounded-mnf-lg p-5 flex items-start gap-4 hover:border-mnf-cyan/20 transition-all">
                    <div className="text-2xl shrink-0">{a.icon}</div>
                    <div className="flex-1">
                      <div className="font-poppins text-sm font-semibold">{a.title}</div>
                      <div className="text-xs text-mnf-text-muted mb-3">{a.desc}</div>
                      <a href={`/partners/api/partners/assets/${a.file}`} download onClick={() => capturePartnerEvent("partner_asset_downloaded", { asset_name: a.file })} className="inline-flex items-center gap-1.5 text-xs text-mnf-cyan font-semibold hover:underline">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>
                        Download
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
